import { LobbyClient } from 'boardgame.io/client';

const SERVER_URL = import.meta.env.VITE_SERVER_URL || 
  (import.meta.env.DEV ? 'http://localhost:8185' : window.location.origin);

export const lobbyClient = new LobbyClient({ server: SERVER_URL });

export interface GameMetadata {
  gameName: string;
  players: { [id: number]: { name: string } };
  settings: {
    wordLimit: 1 | 2 | 3;
    rounds: number;
    timeLimit: 30 | 60 | null;
    isPrivate: boolean;
    numPlayers: number;
    autoSelectColor: boolean;
    gameMode?: 'cooperative' | 'teams' | 'speed';
  };
}

export async function createRoom(
  playerName: string, 
  settings: GameMetadata['settings']
): Promise<{ roomID: string }> {
  const { matchID } = await lobbyClient.createMatch('guesshue', {
    numPlayers: settings.numPlayers,
    setupData: { settings },
    unlisted: settings.isPrivate,
  });
  
  // Join as player 0
  await joinRoom(matchID, playerName, '0');
  
  return { roomID: matchID };
}

export async function joinRoom(
  roomID: string, 
  playerName: string,
  playerID?: string
): Promise<{ playerID: string; credentials: string }> {
  // Get match info to find available seat
  const match = await lobbyClient.getMatch('guesshue', roomID);
  
  if (!match) {
    throw new Error('Room not found');
  }
  
  // Find first available seat if playerID not specified
  if (!playerID) {
    const availableSeat = match.players.findIndex(p => !p.name);
    if (availableSeat === -1) {
      throw new Error('Room is full');
    }
    playerID = String(availableSeat);
  }
  
  const { playerCredentials } = await lobbyClient.joinMatch(
    'guesshue',
    roomID,
    {
      playerID,
      playerName,
    }
  );
  
  // Store credentials for reconnection
  sessionStorage.setItem(`guesshue-${roomID}-creds`, playerCredentials);
  sessionStorage.setItem(`guesshue-${roomID}-playerID`, playerID);
  
  return { playerID, credentials: playerCredentials };
}

export async function listOpenRooms(): Promise<any[]> {
  const { matches } = await lobbyClient.listMatches('guesshue');
  
  // Filter for open rooms (not full and not private)
  return matches.filter(match => {
    const hasAvailableSeats = match.players.some(p => !p.name);
    const isPublic = !match.unlisted;
    return hasAvailableSeats && isPublic;
  });
}

export async function leaveRoom(roomID: string): Promise<void> {
  const playerID = sessionStorage.getItem(`guesshue-${roomID}-playerID`);
  const credentials = sessionStorage.getItem(`guesshue-${roomID}-creds`);
  
  if (playerID && credentials) {
    await lobbyClient.leaveMatch('guesshue', roomID, {
      playerID,
      credentials,
    });
    
    sessionStorage.removeItem(`guesshue-${roomID}-creds`);
    sessionStorage.removeItem(`guesshue-${roomID}-playerID`);
  }
}